import { useCallback, useRef } from 'react';

import { textIsTooLongForScoring } from 'src/data/common';
import { parseHistogramData } from 'src/data/variation';
import { scoreCopyText } from 'src/graphql/scoring';
import { isAbortError } from 'src/lib/errors';
import { useFreshRef } from 'src/lib/hooks';

const SCORE_TIMEOUT = 300;

export default function useScoreText(projectId, assetType, callback) {
  const timersRef = useRef([]);
  const abortControllersRef = useRef([]);
  const callbackRef = useFreshRef(callback);

  const projectIdRef = useFreshRef(projectId);
  const assetTypeRef = useFreshRef(assetType);

  // If we have timer - cancel and remove it
  const clearScoringTimer = useCallback((index) => {
    const timer = timersRef.current[index];
    if (timer) {
      clearTimeout(timer);
      timersRef.current[index] = null;
    }
  }, []);

  // If we have abort controller - cancel request and remove it
  const cancelScoringRequest = useCallback((index) => {
    const controller = abortControllersRef.current[index];
    if (controller) {
      controller.abort();
      abortControllersRef.current[index] = null;
    }
  }, []);

  const fireScoringRequest = useCallback(
    (index, text) => {
      // Do not try to score empty text or if "asset type" is missing
      if (text.length === 0 || !assetTypeRef.current) {
        callbackRef.current(index, null, null);
        return;
      }

      if (textIsTooLongForScoring(text)) {
        callbackRef.current(index, null, { score: null });
        return;
      }

      // Prepare abort controller, to be able to cancel request in-flight
      const controller = new AbortController();
      abortControllersRef.current[index] = controller;

      // Call for update on BE
      scoreCopyText(
        {
          projectId: projectIdRef.current,
          assetType: assetTypeRef.current,
          text,
        },
        { abortSignal: controller.signal }
      )
        .then((result) => {
          callbackRef.current(index, null, {
            score: result.score,
            supportSize: result.engagementScoreSupportSize,
            histogramData: parseHistogramData(result),
            language: result.language,
            isEnterpriseScoreUsed: result.isEnterpriseScoreUsed,
          });
        })
        .catch((error) => {
          if (isAbortError(error)) {
            return;
          }

          callbackRef.current(index, error);
        })
        .finally(() => {
          // Clear related controller
          abortControllersRef.current[index] = null;
        });
    },
    [callbackRef, projectIdRef, assetTypeRef]
  );

  return useCallback(
    (index, text, timeout = SCORE_TIMEOUT) => {
      // Cancel previous timer, if any
      clearScoringTimer(index);
      cancelScoringRequest(index);

      // Create and remember timer
      timersRef.current[index] = setTimeout(() => {
        fireScoringRequest(index, text);
        clearScoringTimer(index);
      }, timeout);
    },
    [cancelScoringRequest, clearScoringTimer, fireScoringRequest]
  );
}
